/**
 * L1J Web Client - MiniMap Component
 * 미니맵 UI 컴포넌트 (우상단 배치)
 *
 * ── 좌표계 규약 ────────────────────────────────────────────────────────────
 * 이 컴포넌트가 외부에서 받는 좌표(setPlayerPosition / setEntities)는
 * **전부 서버 타일 좌표**다. 화면 픽셀 좌표를 넘기면 안 된다.
 * (게임 화면은 아이소메트릭 픽셀로 렌더링하지만, 논리 위치는 항상 타일 좌표.)
 *
 * ── 렌더링 방식 ────────────────────────────────────────────────────────────
 * 미니맵도 게임 화면과 같은 2:1 아이소메트릭 변환을 적용한다.
 * 이유:
 *   1) 방향감 일치 — 게임 화면에서 오른쪽 위에 보이는 몹은 미니맵에서도
 *      오른쪽 위에 찍힌다. 직교 매핑이면 서버 북(ty-1)이 미니맵에선 위,
 *      게임 화면에선 오른쪽 위라 45도 어긋나 오독하기 쉽다.
 *   2) 서버 가시 범위와 프레임 모양이 맞는다 — 서버 Point.isInScreen 은
 *      맨해튼 거리(|dx|+|dy| <= 19) 판정인데, 아이소메트릭 변환에서
 *      u = dtx - dty, v = dtx + dty 로 두면 맨해튼 거리는 max(|u|,|v|) 이므로
 *      "맨해튼 원"이 화면축에 정렬된 **직사각형**이 된다. 즉 사각 프레임에
 *      가시 범위가 정확히 들어맞는다. (직교 매핑이면 45도 기울어진 마름모)
 *
 * 플레이어는 항상 미니맵 정중앙이고, 프레임 밖으로 나가는 마커는 숨긴다.
 */

import Phaser from 'phaser';
import { HEADING_VECTORS, tileDistance } from '../../utils/isometric';
import { gameState } from '../../core/GameState';

export interface MapPoint {
  /** 서버 타일 X 좌표 */
  x: number;
  /** 서버 타일 Y 좌표 */
  y: number;
  type: 'player' | 'npc' | 'monster' | 'item' | 'portal';
  name?: string;
}

/** 미니맵 픽셀 크기 (정사각) */
const MINIMAP_SIZE = 140;

/** 재사용 마커 풀 크기. 서버가 한 화면에 보내는 오브젝트 수를 넉넉히 커버한다. */
const MAX_ENTITY_MARKERS = 64;

/**
 * 기본 표시 반경 (u축 = 화면 가로축 기준 타일 수).
 * 서버는 맨해튼 19타일 밖의 오브젝트를 아예 보내주지 않으므로
 * 그보다 약간 넓은 값이면 충분하다. 너무 넓으면 마커가 중앙에 뭉친다.
 */
const DEFAULT_VIEW_RANGE_TILES = 24;

/** 서버 가시/상호작용 범위 (jp.l1j.server.types.Point#isInScreen: |dx|+|dy| <= 19) */
const SERVER_VISIBLE_RANGE = 19;

/** 배경 격자 간격 (타일) */
const GRID_STEP_TILES = 8;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;

/** 엔티티 종류별 마커 색상 */
const MARKER_COLORS: Record<MapPoint['type'], number> = {
  player: 0x66ddff,  // 하늘색 — 다른 플레이어
  npc: 0xffcc44,     // 노랑 — NPC
  monster: 0xff4444, // 빨강 — 몬스터
  item: 0x44ff88,    // 초록 — 드롭 아이템
  portal: 0xaa66ff,  // 보라 — 포탈
};

export class MiniMap extends Phaser.GameObjects.Container {
  private mapWidth: number = MINIMAP_SIZE;
  private mapHeight: number = MINIMAP_SIZE;

  private mapGraphics!: Phaser.GameObjects.Graphics;
  private playerMarker!: Phaser.GameObjects.Graphics;
  private entityMarkers: Phaser.GameObjects.Graphics[] = [];
  private titleText!: Phaser.GameObjects.Text;
  private coordsText!: Phaser.GameObjects.Text;
  /** 좌상단 '[M]' 토글 힌트. 프레임 위쪽으로 삐져나오므로 히트 영역 계산에 쓴다. */
  private hintText!: Phaser.GameObjects.Text;

  // ── 프레임 치수 (createUI와 containsScreenPoint가 공유한다) ──
  // 두 곳이 같은 값을 봐야 "그려진 테두리"와 "클릭 판정 영역"이 어긋나지 않는다.
  /** 맵 영역 좌우로 프레임이 더 나가는 여백 */
  private readonly framePadX: number = 6;
  /** 맵 영역 위아래로 프레임이 더 나가는 여백 */
  private readonly framePadY: number = 6;
  /** 프레임 상단의 제목줄('MAP nnn') 높이 */
  private readonly titleHeight: number = 16;

  private currentMapId: number = 0;
  /** 플레이어 타일 좌표 */
  private playerX: number = 0;
  private playerY: number = 0;
  private playerHeading: number = 0;

  private entities: MapPoint[] = [];

  /** 현재 표시 반경(타일). setZoom()으로 바뀐다. */
  private viewRangeTiles: number = DEFAULT_VIEW_RANGE_TILES;
  private zoomLevel: number = 1;

  /** 타일 1칸의 미니맵상 가로/세로 반지름 (2:1 비율 유지) */
  private halfTileW: number = 1;
  private halfTileH: number = 0.5;

  private isVisible: boolean = true;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    // 미니맵은 화면에 고정
    this.setScrollFactor(0);

    // 맵 번호의 초기값은 GameState에서 직접 읽는다.
    // S_MapID는 CharSelectScene이 떠 있는 동안 도착할 수 있어, GameScene이
    // map_id를 구독하기 전에 지나가 버린다 (최초 접속 시 제목이 'MAP'으로만
    // 남던 원인). GameState는 모듈 로드 시점부터 networkEvents를 구독하고 있어
    // 씬 전환과 무관하게 최신 mapId를 들고 있으므로 여기서 물어보면 된다.
    this.currentMapId = gameState.getMapId();

    this.recalcScale();
    this.createUI();
    this.setDepth(50);

    this.redrawBackground();
    this.updatePlayerMarker();
  }

  // ───────────────────────────────────────────────────────────── UI 구성

  private createUI(): void {
    const padX = this.framePadX;
    const padY = this.framePadY;
    const titleH = this.titleHeight;

    // 미니맵 프레임 (배경)
    const frame = this.scene.add.graphics();
    frame.fillStyle(0x1a1a2e, 0.95);
    frame.fillRoundedRect(-padX, -padY - titleH, this.mapWidth + padX * 2, this.mapHeight + padY * 2 + titleH, 8);
    frame.lineStyle(2, 0x4a3a14, 1);
    frame.strokeRoundedRect(-padX, -padY - titleH, this.mapWidth + padX * 2, this.mapHeight + padY * 2 + titleH, 8);
    this.add(frame);

    // 타이틀 (미니맵 상단) — 생성 시점에 이미 알고 있는 맵 번호를 반영한다
    this.titleText = this.scene.add.text(this.mapWidth / 2, -titleH / 2, this.mapTitleLabel(), {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold'
    });
    this.titleText.setOrigin(0.5);
    this.add(this.titleText);

    // 맵 배경
    const mapBg = this.scene.add.graphics();
    mapBg.fillStyle(0x0a0a15, 1);
    mapBg.fillRoundedRect(0, 0, this.mapWidth, this.mapHeight, 4);
    this.add(mapBg);

    // 격자 + 가시 범위 외곽선
    this.mapGraphics = this.scene.add.graphics();
    this.add(this.mapGraphics);

    // 서버 기준 북(N) 방향 표시.
    // 아이소메트릭에서 북(dty = -1)은 화면상 오른쪽 위를 향하므로 방향감 힌트가 필요하다.
    const north = this.northLabelPosition(60);
    const northText = this.scene.add.text(north.x, north.y, 'N', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: '#88aacc',
      fontStyle: 'bold'
    });
    northText.setOrigin(0.5);
    northText.setAlpha(0.6);
    this.add(northText);

    // 엔티티 마커 풀
    for (let i = 0; i < MAX_ENTITY_MARKERS; i++) {
      const marker = this.scene.add.graphics();
      marker.setVisible(false);
      this.entityMarkers.push(marker);
      this.add(marker);
    }

    // 플레이어 마커 (화살표) — 항상 마커들 위에
    this.playerMarker = this.scene.add.graphics();
    this.add(this.playerMarker);

    // 좌표 표시
    this.coordsText = this.scene.add.text(this.mapWidth / 2, this.mapHeight + 8, '0, 0', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#cccccc',
      fontStyle: 'bold'
    });
    this.coordsText.setOrigin(0.5);
    this.coordsText.name = 'coordsText';
    this.add(this.coordsText);

    // 토글 힌트 (좌상단)
    this.hintText = this.scene.add.text(-padX, -titleH - 2, '[M]', {
      fontSize: '9px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888'
    });
    this.hintText.setOrigin(0, 1);
    this.add(this.hintText);
  }

  // ───────────────────────────────────────────────────────── 공개 API

  /**
   * 미니맵 표시/숨기기 토글
   */
  public toggle(): void {
    this.setVisible(!this.isVisible);
  }

  /**
   * 현재 맵 설정.
   *
   * mapId가 0 이하로 들어오면(아직 S_MapID를 못 받은 씬이 부르는 경우)
   * 값을 지우지 않고 GameState가 들고 있는 값으로 되돌린다. 맵 0은
   * map_ids 테이블에 실재하는 맵이지만, 여기 오는 0은 "모른다"에 가깝다.
   */
  public setMap(mapId: number): void {
    this.currentMapId = mapId > 0 ? mapId : gameState.getMapId();
    this.refreshTitle();
    this.redrawBackground();
  }

  /**
   * 플레이어 위치 업데이트 (서버 타일 좌표)
   */
  public setPlayerPosition(x: number, y: number, heading: number): void {
    this.playerX = x;
    this.playerY = y;
    this.playerHeading = heading;

    // 격자는 타일 좌표에 고정돼 있어 플레이어가 움직이면 흘러가야 한다.
    this.redrawBackground();
    this.updatePlayerMarker();
    this.updateCoordsText();

    // 마커가 플레이어 상대 좌표이므로 플레이어가 움직이면 전부 다시 계산한다.
    this.updateEntityMarkers();
  }

  /**
   * 엔티티 추가/업데이트 (좌표는 서버 타일 좌표)
   */
  public setEntities(entities: MapPoint[]): void {
    this.entities = entities;
    this.updateEntityMarkers();
  }

  /**
   * 줌 레벨 설정. 1 = 기본, 값이 클수록 확대(표시 반경 축소).
   */
  public setZoom(level: number): void {
    const zoom = Phaser.Math.Clamp(level || 1, ZOOM_MIN, ZOOM_MAX);
    if (zoom === this.zoomLevel) return;

    this.zoomLevel = zoom;
    this.viewRangeTiles = DEFAULT_VIEW_RANGE_TILES / zoom;
    this.recalcScale();

    this.redrawBackground();
    this.updatePlayerMarker();
    this.updateEntityMarkers();
  }

  /**
   * 표시 여부 설정
   */
  public setVisible(visible: boolean): this {
    super.setVisible(visible);
    this.isVisible = visible;
    return this;
  }

  /**
   * 화면 좌표(UI 좌표)가 미니맵 패널 위인지 판정한다.
   *
   * 미니맵 클릭이 월드로 새어 캐릭터가 그쪽으로 걸어가던 문제를 막기 위해
   * GameScene의 isPointerOverOpenPanels()가 이 함수를 쓴다.
   *
   * ── 계산 근거 ──────────────────────────────────────────────────────────
   * 1) **원점**: 이 컨테이너의 로컬 (0, 0)은 프레임 좌상단이 아니라 **맵 영역의
   *    좌상단**이다 (createUI에서 mapBg를 (0,0)~(mapWidth,mapHeight)에 그린다).
   *    프레임은 거기서 왼쪽/위로 더 나간다. 다른 창(중앙 원점)과 규약이 달라
   *    중앙 기준 헬퍼를 그대로 쓸 수 없다.
   * 2) **스크롤팩터**: 생성자에서 setScrollFactor(0)이므로 컨테이너의 x/y는
   *    이미 화면 좌표다. 카메라 스크롤을 빼줄 필요가 없다.
   * 3) **크기**: 프레임은 (-framePadX, -framePadY - titleHeight) 에서
   *    (mapWidth + framePadX, mapHeight + framePadY) 까지다
   *    (= 기본값에서 152 x 168). 여기에 프레임 밖으로 삐져나오는 두 텍스트를
   *    더한다 — 위쪽의 '[M]' 힌트, 아래쪽의 좌표 표시. 폰트 렌더 결과에 따라
   *    높이가 달라지므로 상수로 박지 않고 텍스트 객체의 실측 height를 쓴다.
   * 4) 컨테이너에 스케일을 걸 수도 있으므로 scaleX/scaleY를 곱한다.
   */
  public containsScreenPoint(screenX: number, screenY: number): boolean {
    // 숨겨져 있으면(=[M] 토글로 접은 상태) 클릭을 가로채지 않는다.
    if (!this.isVisible || !this.visible) return false;

    // 프레임 (제목줄 포함) — 컨테이너 로컬 좌표
    let left = -this.framePadX;
    let top = -this.framePadY - this.titleHeight;
    let right = this.mapWidth + this.framePadX;
    let bottom = this.mapHeight + this.framePadY;

    // '[M]' 힌트: origin (0, 1) 이므로 y = -titleHeight - 2 가 **아랫변**이다.
    if (this.hintText) {
      top = Math.min(top, (-this.titleHeight - 2) - this.hintText.height);
    }
    // 좌표 텍스트: origin (0.5, 0.5) 이므로 y = mapHeight + 8 이 **중심**이다.
    if (this.coordsText) {
      bottom = Math.max(bottom, this.mapHeight + 8 + this.coordsText.height / 2);
    }

    // 로컬 -> 화면 (scrollFactor 0 이므로 this.x/this.y가 곧 화면 좌표)
    left = this.x + left * this.scaleX;
    right = this.x + right * this.scaleX;
    top = this.y + top * this.scaleY;
    bottom = this.y + bottom * this.scaleY;

    return screenX >= left && screenX <= right && screenY >= top && screenY <= bottom;
  }

  // ─────────────────────────────────────────────────── 좌표 변환 / 스케일

  private recalcScale(): void {
    // u축(화면 가로) 기준으로 viewRangeTiles 타일이 미니맵 절반 폭에 들어오게 한다.
    this.halfTileW = (this.mapWidth / 2) / this.viewRangeTiles;
    // 2:1 아이소메트릭 비율 유지 (isometric.ts의 TILE_WIDTH : TILE_HEIGHT = 2 : 1)
    this.halfTileH = this.halfTileW / 2;
  }

  /**
   * 서버 타일 좌표 -> 미니맵 로컬 픽셀 좌표 (플레이어 중심 아이소메트릭).
   * isometric.ts의 tileToWorld와 같은 식이되, 플레이어 기준 상대 좌표에
   * 미니맵 전용 타일 크기를 적용한다.
   */
  private tileToMap(tileX: number, tileY: number): { mapX: number; mapY: number } {
    const dx = tileX - this.playerX;
    const dy = tileY - this.playerY;

    return {
      mapX: this.mapWidth / 2 + (dx - dy) * this.halfTileW,
      mapY: this.mapHeight / 2 + (dx + dy) * this.halfTileH
    };
  }

  /**
   * 미니맵 프레임 안에 들어오는 점인지. 범위 밖 마커는 숨겨 프레임 밖으로
   * 삐져나가는 것을 막는다. (마커 반지름만큼 여유를 둔다)
   */
  private isInsideFrame(mapX: number, mapY: number, margin: number = 3): boolean {
    return (
      mapX >= margin && mapX <= this.mapWidth - margin &&
      mapY >= margin && mapY <= this.mapHeight - margin
    );
  }

  /** 화면상 북(N) 방향 라벨 위치 (중심에서 radius 픽셀) */
  private northLabelPosition(radius: number): { x: number; y: number } {
    // 북 = 타일 델타 (0, -1) -> (dx - dy, dx + dy) = (1, -1)
    // 2:1 비율이므로 화면 방향 벡터는 (2, -1) / sqrt(5)
    const len = Math.sqrt(5);
    return {
      x: this.mapWidth / 2 + (2 / len) * radius,
      y: this.mapHeight / 2 + (-1 / len) * radius
    };
  }

  // ───────────────────────────────────────────────────────── 렌더링

  /**
   * 배경(아이소메트릭 격자) + 서버 가시 범위 외곽선 렌더링.
   * 실제 맵 타일 데이터는 아직 없으므로 격자로 대체한다.
   */
  private redrawBackground(): void {
    this.mapGraphics.clear();
    this.drawIsoGrid();
    this.drawVisibleRange();
  }

  /**
   * 타일 좌표에 고정된 아이소메트릭 격자.
   * tx 고정 선과 ty 고정 선이 각각 화면상 ±26.5도 기울기로 그려진다.
   */
  private drawIsoGrid(): void {
    // 프레임을 덮으려면 |dtx| <= 1.5 * viewRange 정도면 충분하다.
    const span = Math.ceil(this.viewRangeTiles * 1.5) + GRID_STEP_TILES;
    const baseX = Math.round(this.playerX);
    const baseY = Math.round(this.playerY);

    const minTx = Math.floor((baseX - span) / GRID_STEP_TILES) * GRID_STEP_TILES;
    const maxTx = baseX + span;
    const minTy = Math.floor((baseY - span) / GRID_STEP_TILES) * GRID_STEP_TILES;
    const maxTy = baseY + span;

    this.mapGraphics.lineStyle(1, 0x2a3a4a, 0.35);

    // tx 고정 (ty 방향으로 뻗는 선)
    for (let tx = minTx; tx <= maxTx; tx += GRID_STEP_TILES) {
      const a = this.tileToMap(tx, baseY - span);
      const b = this.tileToMap(tx, baseY + span);
      this.strokeClippedLine(a.mapX, a.mapY, b.mapX, b.mapY);
    }

    // ty 고정 (tx 방향으로 뻗는 선)
    for (let ty = minTy; ty <= maxTy; ty += GRID_STEP_TILES) {
      const a = this.tileToMap(baseX - span, ty);
      const b = this.tileToMap(baseX + span, ty);
      this.strokeClippedLine(a.mapX, a.mapY, b.mapX, b.mapY);
    }
  }

  /**
   * 서버 가시/상호작용 범위(맨해튼 19타일) 외곽선.
   *
   * 아이소메트릭 변환에서 u = dtx - dty, v = dtx + dty 이고
   * |dtx| + |dty| == max(|u|, |v|) 이므로, 맨해튼 거리 R 영역은
   * 미니맵 상에서 반너비 R*halfTileW, 반높이 R*halfTileH 인 직사각형이 된다.
   */
  private drawVisibleRange(): void {
    const cx = this.mapWidth / 2;
    const cy = this.mapHeight / 2;

    const halfW = Math.min(SERVER_VISIBLE_RANGE * this.halfTileW, cx - 1);
    const halfH = Math.min(SERVER_VISIBLE_RANGE * this.halfTileH, cy - 1);

    this.mapGraphics.fillStyle(0x66ccff, 0.05);
    this.mapGraphics.fillRect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);

    this.mapGraphics.lineStyle(1, 0x66ccff, 0.4);
    this.mapGraphics.strokeRect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);
  }

  /**
   * 플레이어 마커 (항상 중앙, heading 방향 화살표).
   * heading은 서버 타일 기준이므로 아이소메트릭 변환을 거쳐 화면 각도로 바꾼다.
   */
  private updatePlayerMarker(): void {
    this.playerMarker.clear();

    const cx = this.mapWidth / 2;
    const cy = this.mapHeight / 2;

    const vec = HEADING_VECTORS[((this.playerHeading % 8) + 8) % 8];
    // 타일 델타 -> 미니맵 방향 벡터 (2:1 아이소메트릭)
    const dirX = (vec.dx - vec.dy) * 2;
    const dirY = (vec.dx + vec.dy) * 1;
    const len = Math.hypot(dirX, dirY) || 1;
    const angle = Math.atan2(dirY / len, dirX / len);

    const size = 6;

    this.playerMarker.fillStyle(0x00ff66, 1);
    this.playerMarker.beginPath();
    this.playerMarker.moveTo(
      cx + Math.cos(angle) * size,
      cy + Math.sin(angle) * size
    );
    this.playerMarker.lineTo(
      cx + Math.cos(angle + 2.5) * size * 0.7,
      cy + Math.sin(angle + 2.5) * size * 0.7
    );
    this.playerMarker.lineTo(
      cx + Math.cos(angle - 2.5) * size * 0.7,
      cy + Math.sin(angle - 2.5) * size * 0.7
    );
    this.playerMarker.closePath();
    this.playerMarker.fillPath();
  }

  /**
   * 엔티티 마커 업데이트.
   * 프레임 안에 들어오는 엔티티만 마커 풀에서 꺼내 그린다.
   */
  private updateEntityMarkers(): void {
    let used = 0;

    for (const entity of this.entities) {
      if (used >= MAX_ENTITY_MARKERS) break;

      const { mapX, mapY } = this.tileToMap(entity.x, entity.y);
      if (!this.isInsideFrame(mapX, mapY)) continue;

      const marker = this.entityMarkers[used++];
      marker.clear();

      const color = MARKER_COLORS[entity.type] ?? 0xffffff;
      // 서버 가시 범위 안이면 진하게, 밖이면 흐리게 — 상호작용 가능 여부를 구분한다.
      const inRange = tileDistance(entity.x, entity.y, this.playerX, this.playerY) <= SERVER_VISIBLE_RANGE;
      const alpha = inRange ? 0.95 : 0.4;

      this.drawEntityShape(marker, entity.type, mapX, mapY, color, alpha);
      marker.setVisible(true);
    }

    for (let i = used; i < this.entityMarkers.length; i++) {
      this.entityMarkers[i].setVisible(false);
    }
  }

  /**
   * 종류별 마커 모양. 색만으로 구분하지 않고 모양도 달리해 색약 사용자도 구분 가능하게 한다.
   */
  private drawEntityShape(
    g: Phaser.GameObjects.Graphics,
    type: MapPoint['type'],
    x: number,
    y: number,
    color: number,
    alpha: number
  ): void {
    g.fillStyle(color, alpha);

    switch (type) {
      case 'player':
        // 사각형
        g.fillRect(x - 2.5, y - 2.5, 5, 5);
        g.lineStyle(1, 0xffffff, alpha * 0.8);
        g.strokeRect(x - 2.5, y - 2.5, 5, 5);
        break;

      case 'monster':
        // 마름모
        g.fillPoints([
          new Phaser.Geom.Point(x, y - 4),
          new Phaser.Geom.Point(x + 3, y),
          new Phaser.Geom.Point(x, y + 4),
          new Phaser.Geom.Point(x - 3, y)
        ], true);
        break;

      case 'portal':
        // 링
        g.lineStyle(2, color, alpha);
        g.strokeCircle(x, y, 3);
        break;

      case 'item':
        g.fillCircle(x, y, 1.5);
        break;

      case 'npc':
      default:
        g.fillCircle(x, y, 3);
        break;
    }
  }

  /**
   * 제목줄 문자열. 맵 번호는 최대 5자리(map_ids 최대 id = 25088)라
   * 'MAP 25088' 9글자가 최장이고, 10px Arial bold 기준 약 53px이다.
   * 프레임 안쪽 폭 152px 안에 여유 있게 들어가므로 줄임 처리는 필요 없다.
   */
  private mapTitleLabel(): string {
    return this.currentMapId > 0 ? `MAP ${this.currentMapId}` : 'MAP';
  }

  private refreshTitle(): void {
    this.titleText.setText(this.mapTitleLabel());
  }

  private updateCoordsText(): void {
    this.coordsText.setText(`${Math.floor(this.playerX)}, ${Math.floor(this.playerY)}`);
  }

  // ─────────────────────────────────────────────────────────── 유틸

  /**
   * 선분을 미니맵 사각 영역으로 클리핑해서 그린다 (Liang-Barsky).
   * 격자선이 프레임 밖으로 새어 나가는 것을 막는다.
   */
  private strokeClippedLine(x0: number, y0: number, x1: number, y1: number): void {
    const dx = x1 - x0;
    const dy = y1 - y0;

    let t0 = 0;
    let t1 = 1;

    const p = [-dx, dx, -dy, dy];
    const q = [x0 - 0, this.mapWidth - x0, y0 - 0, this.mapHeight - y0];

    for (let i = 0; i < 4; i++) {
      if (p[i] === 0) {
        if (q[i] < 0) return; // 경계와 평행하면서 바깥 — 완전히 제외
        continue;
      }
      const r = q[i] / p[i];
      if (p[i] < 0) {
        if (r > t1) return;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return;
        if (r < t1) t1 = r;
      }
    }

    this.mapGraphics.lineBetween(
      x0 + t0 * dx,
      y0 + t0 * dy,
      x0 + t1 * dx,
      y0 + t1 * dy
    );
  }
}
